import { useState } from 'react'
import { Trans, useTranslation } from 'react-i18next'
import { useNavigate } from 'react-router-dom'

import { MaterialIcon } from '@makyoapp/makyo-tw-ui'
import { Button, ButtonVariant, Modal } from '@makyoapp/makyo-ui'

import { useGetBrandKits } from '@/resolvers/org/brand-kit/query'
import { CreateDbProvider } from '@/context/CreateDbContext'
import { useCurrentOrgContext } from '@/context/CurrentOrgContext'
import { AppFilterProvider, useOrgAppsContext } from '@/context/OrgAppsContext'
import useModal from '@/hooks/useModal'
import { ORG_BRAND_KIT_ROUTE, ORG_ROUTE } from '@/shared/constants'
import { WrapperProps } from '@/shared/routes'
import Layout from '@/components/Layout'

import AddNewAppModal from './components/AddNewAppModal'
import GenerateNewAppModal from './components/GenerateNewAppModal'
import { useOrgAppAction } from './hooks/useOrgAppAction'

export type GenerateAppType = 'ecommerce' | 'text' | 'database' | 'website' | 'template'

function OrgAddNewApp({ title }: WrapperProps) {
  const navigate = useNavigate()
  const { t } = useTranslation()

  const { currentOrg } = useCurrentOrgContext()
  const { handleAddNewApp } = useOrgAppAction()
  const { setShowAdd } = useOrgAppsContext()

  const { show: showGeneratorModal, toggleVisibility: toggleGeneratorModal } = useModal()
  const [generateAppType, setGenerateAppType] = useState<GenerateAppType>('template')
  const [showNoBrandKitModal, setShowNoBrandKitModal] = useState(false)

  // Access org brand kits
  const brandkitsQuery = useGetBrandKits()
  const noBrandKit = brandkitsQuery.isFetched && !brandkitsQuery.data?.length

  const handleOptionClick = (type: GenerateAppType, isAi: boolean) => {
    toggleGeneratorModal()
    setGenerateAppType(type)
  }

  const showAddNewAppModal = () => {
    if (noBrandKit) {
      setShowNoBrandKitModal(true)
    } else {
      setShowAdd(true)
    }
  }

  const addAppOptions = [
    {
      title: t('org.add_app.from_scratch.title'),
      description: t('org.add_app.from_scratch.description'),
      icon: 'edit',
      onClick: showAddNewAppModal,
      isAi: false,
    },
    {
      title: t('org.add_app.from_template.title'),
      description: t('org.add_app.from_template.description'),
      icon: 'grid_on',
      onClick: () => handleOptionClick('template', false),
      isAi: false,
    },
    {
      title: t('org.add_app.from_website.title'),
      description: t('org.add_app.from_website.description'),
      icon: 'web',
      onClick: () => handleOptionClick('website', true),
      isAi: true,
    },
    {
      title: t('org.add_app.ecommerce.title'),
      description: t('org.add_app.ecommerce.description'),
      icon: 'shopping_cart',
      onClick: () => handleOptionClick('ecommerce', true),
      isAi: true,
    },
    {
      title: t('org.add_app.from_database.title'),
      description: t('org.add_app.from_database.description'),
      icon: 'database',
      onClick: () => handleOptionClick('database', true),
      isAi: true,
    },
    {
      title: t('org.add_app.from_text.title'),
      description: t('org.add_app.from_text.description'),
      icon: 'text_fields',
      onClick: () => handleOptionClick('text', true),
      isAi: true,
    },
  ]

  return (
    <Layout title={title} wrapClassName='h-full'>
      <div className='py-4'>
        <p className='text-base font-bold mb-4'>{t('org.add_app.create_app.title')}</p>

        <div className='grid grid-cols-3 gap-8'>
          {addAppOptions.map((option) => (
            <div
              key={option.title}
              onClick={option.onClick}
              className='w-full h-48 p-6 group flex flex-col gap-4 items-center font-semibold text-zinc-500 dark:text-slate-400 uppercase bg-zinc-50 dark:bg-slate-800 rounded-xl shadow-sm border border-zinc-200 dark:border-slate-500 hover:border-teal-500 hover:ring-1 hover:ring-inset hover:ring-teal-500 dark:hover:ring-slate-500 transition-all'
            >
              <MaterialIcon icon={option.icon} size={40} className='text-primary-500' />
              <div className='flex items-center gap-2'>
                <p className='normal-case text-base'>{option.title}</p>
                {option.isAi && <span className='ai-badge'>AI</span>}
              </div>
              <p className='text-center text-sm text-gray-400 normal-case'>{option.description}</p>
            </div>
          ))}

          <CreateDbProvider backButtonText='Back'>
            <AddNewAppModal onAdd={handleAddNewApp} hideButton />
          </CreateDbProvider>

          <GenerateNewAppModal closeModal={toggleGeneratorModal} showAdd={showGeneratorModal} type={generateAppType} />

          <Modal
            id='org-detail-no-brand-kit-modal'
            show={showNoBrandKitModal}
            onHide={() => setShowNoBrandKitModal(false)}
          >
            <Modal.Header onCloseClick={() => setShowNoBrandKitModal(false)}>
              <Trans>Setup Your First Brand Kit</Trans>
            </Modal.Header>
            <Modal.Body className='modal-md min-h-[150px]'>
              <p>
                <Trans>
                  Before you can create a new app, you will need to set up a brand kit first. A brand kit helps ensure
                  your app maintains consistent styling and branding throughout.
                </Trans>
                <br />
              </p>
            </Modal.Body>
            <Modal.Footer className='justify-between'>
              <Button variant={ButtonVariant.Outlined} onClick={() => setShowNoBrandKitModal(false)}>
                <Trans>Cancel</Trans>
              </Button>
              <Button
                variant={ButtonVariant.Normal}
                onClick={() => navigate(`${ORG_ROUTE}/${currentOrg?.id}/${ORG_BRAND_KIT_ROUTE}`)}
              >
                <Trans>Go to Brand Kits</Trans>
              </Button>
            </Modal.Footer>
          </Modal>
        </div>
      </div>
    </Layout>
  )
}

export default function OrgAddNewAppPage() {
  const { t } = useTranslation()
  return (
    <AppFilterProvider>
      <OrgAddNewApp title={t('Start an app')} />
    </AppFilterProvider>
  )
}
